// UserProfile Component - Extracted from Dashboard for better maintainability
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { ArrowLeft, Key, Eye, EyeOff, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback } from './ui/avatar';
import PushNotificationManager from './PushNotificationManager';
import { LANGUAGES, getLanguageByCode } from '../constants/languages';
import { getApiUrl } from '../utils/api';
import axios from 'axios';

const UserProfile = ({ 
  currentUser, 
  onClose, 
  onLogout,
  profileEditMode,
  setProfileEditMode,
  profileUserData,
  setProfileUserData,
  profileSaving,
  setProfileSaving
}) => {
  const { t } = useTranslation();
  
  // PIN Management State
  const [showPinSection, setShowPinSection] = useState(false);
  const [hasPin, setHasPin] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [currentPin, setCurrentPin] = useState('');
  const [showNewPin, setShowNewPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const [showCurrentPin, setShowCurrentPin] = useState(false);
  const [pinError, setPinError] = useState('');
  const [pinSuccess, setPinSuccess] = useState('');

  // Check if user has PIN on mount
  useEffect(() => {
    const checkPinStatus = async () => {
      try {
        const response = await axios.get(`${getApiUrl()}/auth/has-pin/${encodeURIComponent(currentUser.phone_number)}`);
        setHasPin(response.data.has_pin);
      } catch (error) {
        console.error('Error checking PIN status:', error);
        setHasPin(false);
      }
    };
    
    if (currentUser?.phone_number) {
      checkPinStatus();
    }
  }, [currentUser?.phone_number]);

  // Handle PIN save (set new or change existing)
  const handlePinSave = async () => {
    setPinError('');
    setPinSuccess('');
    
    // Validation
    if (newPin.length < 4 || newPin.length > 8) {
      setPinError(t('profile.pinLengthError', 'PIN muss zwischen 4 und 8 Ziffern lang sein'));
      return;
    }
    
    if (newPin !== confirmPin) {
      setPinError(t('profile.pinMismatch', 'PINs stimmen nicht überein'));
      return;
    }
    
    if (!/^\d+$/.test(newPin)) {
      setPinError(t('profile.pinDigitsOnly', 'PIN darf nur Ziffern enthalten'));
      return;
    }
    
    // If user already has PIN, require current PIN
    if (hasPin && !currentPin) {
      setPinError(t('profile.currentPinRequired', 'Bitte aktuellen PIN eingeben'));
      return;
    }
    
    setPinLoading(true);
    
    try {
      const requestData = {
        user_id: currentUser.id,
        new_pin: newPin,
      };
      
      // If changing existing PIN, include current PIN for verification
      if (hasPin) {
        requestData.current_pin = currentPin;
      }
      
      const response = await axios.post(`${getApiUrl()}/auth/set-pin`, requestData);
      
      if (response.data.success) {
        setPinSuccess(t('profile.pinSaved', 'PIN erfolgreich gespeichert!'));
        setHasPin(true);
        setNewPin('');
        setConfirmPin('');
        setCurrentPin('');
        setShowPinSection(false);
        
        // Show success message briefly
        setTimeout(() => setPinSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Error saving PIN:', error);
      const errorMessage = error.response?.data?.detail || t('profile.pinSaveError', 'Fehler beim Speichern des PINs');
      setPinError(errorMessage);
    } finally {
      setPinLoading(false);
    }
  };

  // Handle PIN removal
  const handlePinRemove = async () => {
    if (!currentPin) {
      setPinError(t('profile.currentPinRequired', 'Bitte aktuellen PIN eingeben'));
      return;
    }
    
    if (!window.confirm(t('profile.confirmPinRemove', 'Sind Sie sicher, dass Sie Ihren Login-PIN entfernen möchten? Sie können sich dann nur noch per SMS anmelden.'))) {
      return;
    }
    
    setPinLoading(true);
    setPinError('');
    
    try {
      const response = await axios.post(`${getApiUrl()}/auth/remove-pin`, {
        user_id: currentUser.id,
        current_pin: currentPin
      });
      
      if (response.data.success) {
        setPinSuccess(t('profile.pinRemoved', 'PIN erfolgreich entfernt'));
        setHasPin(false);
        setCurrentPin('');
        setShowPinSection(false);
        setTimeout(() => setPinSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Error removing PIN:', error);
      const errorMessage = error.response?.data?.detail || t('profile.pinRemoveError', 'Fehler beim Entfernen des PINs');
      setPinError(errorMessage);
    } finally {
      setPinLoading(false);
    }
  };

  const handleSave = async () => {
    setProfileSaving(true);
    try {
      const response = await fetch(`${getApiUrl()}/users/${currentUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileUserData)
      });
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(t('errors.serverError', 'Server error: Please deploy the latest version'));
      }
      
      if (response.ok) {
        const updatedData = await response.json();
        const updatedUser = { ...currentUser, ...updatedData };
        localStorage.setItem('currentUser', JSON.stringify(updatedUser));
        setProfileUserData({
          name: updatedUser.name || '',
          company: updatedUser.company || '',
          phone_number: updatedUser.phone_number || '',
          language: updatedUser.language || 'de'
        });
        // Change app language when profile language changes
        if (updatedUser.language) {
          i18n.changeLanguage(updatedUser.language);
          localStorage.setItem('userLanguage', updatedUser.language);
        }
        alert(`✅ ${t('profile.saveSuccess')}`);
        setProfileEditMode(false);
      } else {
        const error = await response.json();
        alert(`❌ ${t('common.error')}: ` + (error.detail || t('errors.unknown')));
      }
    } catch (error) {
      alert(`❌ ${t('profile.saveError')}: ` + error.message);
    }
    setProfileSaving(false);
  };

  return (
    <div className="flex flex-col bg-white" style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0,
      height: '100dvh',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div className="p-4 bg-white border-b shadow-sm" style={{ flexShrink: 0 }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={onClose}
              className="mr-3 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold text-gray-800">{t('profile.title')}</h1>
          </div>
        </div>
      </div>

      {/* Content - Scrollbar */}
      <div className="p-4" style={{ 
        flex: 1, 
        minHeight: 0, 
        overflowY: 'auto', 
        WebkitOverflowScrolling: 'touch',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 20px) + 20px)'
      }}>
        {/* Avatar Section */}
        <div className="flex flex-col items-center mb-6">
          <Avatar className="w-20 h-20 mb-3">
            <AvatarFallback className="bg-emerald-500 text-white text-2xl">
              {currentUser.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <h2 className="text-lg font-semibold text-gray-800">{currentUser.name}</h2>
          <p className="text-sm text-gray-500">{currentUser.company}</p>
        </div>

        {/* Profile Fields */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.name')}</label>
            {profileEditMode ? (
              <Input
                value={profileUserData.name}
                onChange={(e) => setProfileUserData({...profileUserData, name: e.target.value})}
                className="w-full"
              />
            ) : (
              <p className="p-2 bg-gray-50 rounded-lg text-gray-800">{currentUser.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.company')}</label>
            {profileEditMode ? (
              <Input
                value={profileUserData.company}
                onChange={(e) => setProfileUserData({...profileUserData, company: e.target.value})}
                className="w-full"
              />
            ) : (
              <p className="p-2 bg-gray-50 rounded-lg text-gray-800">{currentUser.company || '-'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.phone')}</label>
            {profileEditMode ? (
              <Input
                type="tel"
                value={profileUserData.phone_number}
                onChange={(e) => setProfileUserData({...profileUserData, phone_number: e.target.value})}
                className="w-full"
                placeholder="+49..."
              />
            ) : (
              <p className="p-2 bg-gray-50 rounded-lg text-gray-800">{currentUser.phone_number}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.language')}</label>
            {profileEditMode ? (
              <select
                value={profileUserData.language}
                onChange={(e) => setProfileUserData({...profileUserData, language: e.target.value})}
                className="w-full p-2 border rounded-lg"
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name}
                  </option>
                ))}
              </select>
            ) : (
              <p className="p-2 bg-gray-50 rounded-lg text-gray-800">
                {getLanguageByCode(profileUserData.language)?.flag} {getLanguageByCode(profileUserData.language)?.name}
              </p>
            )}
          </div>
        </div>

        {/* PIN Management Section */}
        <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-gray-800">{t('profile.loginPin', 'Login-PIN')}</h3>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${hasPin ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
              {hasPin ? t('profile.pinActive', 'Aktiv') : t('profile.pinNotSet', 'Nicht eingerichtet')}
            </span>
          </div>
          
          <p className="text-sm text-gray-600 mb-3">
            {t('profile.pinDescription', 'Mit einem Login-PIN können Sie sich auf Geräten ohne SIM-Karte (z.B. Tablets) anmelden, ohne SMS zu benötigen.')}
          </p>
          
          {/* Success/Error Messages */}
          {pinSuccess && (
            <div className="mb-3 p-2 bg-green-100 border border-green-300 rounded-lg flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700">{pinSuccess}</span>
            </div>
          )}
          
          {pinError && (
            <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded-lg flex items-center gap-2">
              <X className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-700">{pinError}</span>
            </div>
          )}
          
          {!showPinSection ? (
            <Button
              variant="outline"
              className="w-full border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={() => {
                setShowPinSection(true);
                setPinError('');
                setPinSuccess('');
              }}
            >
              <Key className="w-4 h-4 mr-2" />
              {hasPin ? t('profile.changePin', 'PIN ändern') : t('profile.setPin', 'PIN einrichten')}
            </Button>
          ) : (
            <div className="space-y-3">
              {/* Current PIN (only if user already has one) */}
              {hasPin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.currentPin', 'Aktueller PIN')}
                  </label>
                  <div className="relative">
                    <Input
                      type={showCurrentPin ? "text" : "password"}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={currentPin}
                      onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ''))}
                      placeholder="••••••"
                      maxLength={8}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPin(!showCurrentPin)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showCurrentPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
              
              {/* New PIN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {hasPin ? t('profile.newPin', 'Neuer PIN') : t('profile.enterPin', 'PIN eingeben')}
                </label>
                <div className="relative">
                  <Input
                    type={showNewPin ? "text" : "password"}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                    placeholder="4-8 Ziffern"
                    maxLength={8}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPin(!showNewPin)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {/* Confirm PIN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.confirmPin', 'PIN bestätigen')}
                </label>
                <div className="relative">
                  <Input
                    type={showConfirmPin ? "text" : "password"}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={confirmPin}
                    onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                    placeholder="PIN wiederholen"
                    maxLength={8}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPin(!showConfirmPin)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {newPin && confirmPin && newPin !== confirmPin && (
                  <p className="text-xs text-red-500 mt-1">{t('profile.pinMismatch', 'PINs stimmen nicht überein')}</p>
                )}
                {newPin && confirmPin && newPin === confirmPin && (
                  <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                    <Check className="w-3 h-3" /> {t('profile.pinMatch', 'PINs stimmen überein')}
                  </p>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowPinSection(false);
                    setNewPin('');
                    setConfirmPin('');
                    setCurrentPin('');
                    setPinError('');
                  }}
                  disabled={pinLoading}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={handlePinSave}
                  disabled={pinLoading || !newPin || !confirmPin || newPin !== confirmPin}
                >
                  {pinLoading ? t('common.loading') : t('common.save')}
                </Button>
              </div>
              
              {/* Remove PIN option (only if user has one) */}
              {hasPin && (
                <Button
                  variant="ghost"
                  className="w-full text-red-600 hover:bg-red-50 text-sm"
                  onClick={handlePinRemove}
                  disabled={pinLoading || !currentPin}
                >
                  {t('profile.removePin', 'PIN entfernen')}
                </Button>
              )}
            </div>
          )}
        </div>


        {/* Action Buttons */}
        <div className="mt-6 space-y-3">
          {profileEditMode ? (
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setProfileEditMode(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={handleSave}
                disabled={profileSaving}
              >
                {profileSaving ? `💾 ${t('common.loading')}` : `💾 ${t('common.save')}`}
              </Button>
            </div>
          ) : (
            <Button 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              onClick={() => setProfileEditMode(true)}
            >
              ✏️ {t('profile.editProfile')}
            </Button>
          )}
          
          <Button 
            variant="outline" 
            className="w-full text-red-600 border-red-200 hover:bg-red-50"
            onClick={onLogout}
          >
            🚪 {t('auth.logout')}
          </Button>
        </div>

        {/* Push Notifications Settings */}
        <div className="mt-6">
          <PushNotificationManager userId={currentUser.id} />
        </div>

        {/* Info */}
        <div className="mt-6 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 text-center">
            {t('profile.userId')}: {currentUser.id?.substring(0, 8)}...
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
